// Import utilities for the api.
import { currentTime, each, empty, param } from './Helpers';

/**
 * The main api class that constructs all other components.
 *
 * @since 5.0.0
 */
export default class Request {
	/**
	 * "Class" constructor in ES6.
	 *
	 * @since 5.0.0
	 */
	constructor() {
		// Set some default variables for the class.
		this.response = '';
		this.method = 'GET';
		this.url = '';
		this.timeout = 30000;
		this.cache = 1;
		this.headers = {};
		this.xhr = window.XMLHttpRequest ? new XMLHttpRequest() : new window.ActiveXObject('Microsoft.XMLHTTP');
	}

	/**
	 * This starts up a get request.
	 *
	 * @since 5.0.0
	 *
	 * @param   {string}        url  The url for the request
	 * @param   {Object|string} data The data to be added as query args to the request.
	 * @returns {Request}            Returns the current class for method chaining.
	 */
	get = (url, data) => {
		this.method = 'GET';
		this.url = url;

		if (data) {
			data = 'string' === typeof data ? data : param(data);
			this.url += (/\?/.test(url) ? '&' : '?') + data;
		}

		if (!this.cache) {
			this.url += (/\?/.test(url) ? '&' : '?') + currentTime();
		}
		return this;
	};

	/**
	 * This starts up a post request.
	 *
	 * @since 5.0.0
	 *
	 * @param   {string}        url  The url for the request.
	 * @param   {Object|string} data The data for the request.
	 * @returns {Request}            Returns the current class for method chaining.
	 */
	post = (url, data) => {
		this.method = 'POST';
		this.url = url;
		this.data = data;
		return this;
	};

	/**
	 * Set a timeout on the request.
	 *
	 * @since 5.0.0
	 *
	 * @param   {number} timeout The timeout in milliseconds.
	 * @returns {Request}         Returns the current class for method chaining.
	 */
	setTimeout = (timeout) => {
		this.timeout = timeout;
		return this;
	};

	/**
	 * Enable the cache for HEAD and GET requests or not.
	 *
	 * @since 5.0.0
	 *
	 * @param   {boolean} cache Whether or not to enable the cache.
	 * @returns {Request}       Returns the current class for method chaining.
	 */
	setCache = (cache) => {
		this.cache = cache ? 1 : 0;
		return this;
	};

	/**
	 * Enables setting a header on a request.
	 *
	 * @since 5.20.3
	 *
	 * @param   {string} header The header to set.
	 * @param   {string} value  The header value to set.
	 * @returns {Request}       Returns the current class for method chaining.
	 */
	setHeader = (header, value) => {
		this.headers[header] = value;
		return this;
	};

	/**
	 * Let's make a request!
	 *
	 * @since 5.0.0
	 *
	 * @param   {string}             url  The url for the request.
	 * @param   {Object|string|null} data The data for the request.
	 * @returns {Promise}                 Returns a completed promise once the XHR request is complete.
	 */
	send = () => {
		return new Promise((resolve, reject) => {
			// Parse the data in the correct format based on the data type and method.
			let params =
				'GET' === this.method ? null : 'string' === typeof this.data ? this.data : JSON.stringify(this.data);

			// Let's open up a connection.
			this.xhr.open(this.method, this.url, true);

			// Set the timeout.
			this.xhr.timeout = this.timeout;

			// We need to set some additional headers.
			if ('GET' !== this.method) {
				this.xhr.setRequestHeader('Content-Type', 'application/json');
				this.xhr.setRequestHeader('X-Requested-With', 'OptinMonsterApi');
			}

			// Set request headers if they have been defined.
			if (!empty(this.headers)) {
				each(this.headers, (header, value) => {
					this.xhr.setRequestHeader(header, value);
				});
			}

			// Once the data has loaded, we need to resolve or reject it.
			this.xhr.onload = () => {
				if (3 < this.xhr.readyState && (200 === this.xhr.status || 203 === this.xhr.status)) {
					resolve(this.xhr);
				} else {
					reject(this.xhr);
				}
			};

			// If there are connection errors, reject right away.
			this.xhr.onerror = () => {
				reject(this.xhr);
			};

			// Send the request!
			this.xhr.send(params);
		});
	};
}
